import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Landmark, Sparkles, Plus, Search, Filter, Calendar, X,
  AlertTriangle, Receipt, ToggleLeft, ToggleRight, Edit3, Trash2,
  UploadCloud
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { buildApiUrl } from '../../utils/apiSetup';
import BulkBankLoanModal from './BulkBankLoanModal';

const BankLoanManagement = () => {
  const { hasPermission } = useAuth();
  const { showSuccess, showError, showWarning } = useNotification();
  const canViewLoans = hasPermission('bank:loan:view');
  const canViewBanks = hasPermission('bank:view');

  const [banks, setBanks] = useState([]);
  const [loans, setLoans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tab control
  const [activeTab, setActiveTab] = useState(() => {
    if (canViewLoans) return 'loans';
    if (canViewBanks) return 'banks';
    return 'loans';
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [bankFilter, setBankFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modals state
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [editingBank, setEditingBank] = useState(null);
  const [selectedLoanForStop, setSelectedLoanForStop] = useState(null);

  // Form states
  const createEmptyLoanEntry = (prevEntry = null) => ({
    tempId: Date.now() + Math.random(),
    bank_id: '',
    original_loan_amount: '',
    monthly_deduction_amount: '',
    effective_month: prevEntry ? prevEntry.effective_month : new Date().getMonth() + 1,
    effective_year: prevEntry ? prevEntry.effective_year : new Date().getFullYear(),
    loan_reference_number: ''
  });

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [loanEntries, setLoanEntries] = useState([createEmptyLoanEntry()]);
  const [isSubmittingLoan, setIsSubmittingLoan] = useState(false);

  const [bankForm, setBankForm] = useState({
    bank_name: '',
    bank_code: ''
  });

  const [stopForm, setStopForm] = useState({
    stop_month: new Date().getMonth() + 1,
    stop_year: new Date().getFullYear()
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch Banks
      const banksRes = await fetch(buildApiUrl('/api/v1/bank-loans/banks'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      const banksData = await banksRes.json();
      setBanks(Array.isArray(banksData) ? banksData : []);

      // Fetch Loans
      const loansRes = await fetch(buildApiUrl('/api/v1/bank-loans'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      const loansData = await loansRes.json();
      setLoans(Array.isArray(loansData) ? loansData : []);

      // Fetch Employees for dropdown
      const empRes = await fetch(buildApiUrl('/api/v1/benfish-employees/search?limit=1000'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      const empData = await empRes.json();
      const mappedEmployees = (empData.employees || []).map(emp => ({
        ...emp,
        full_name: emp.full_name || emp.emp_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
      }));
      setEmployees(mappedEmployees);

    } catch (err) {
      console.error('Failed to load bank loan data:', err);
      showError('Failed to load bank loan data');
    } finally {
      setLoading(false);
    }
  };

  // Helper lists
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 2 + i);

  // Bank handlers
  const handleBankSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingBank ? `/api/v1/bank-loans/banks/${editingBank.id}` : '/api/v1/bank-loans/banks';
      const method = editingBank ? 'PUT' : 'POST';
      const res = await fetch(buildApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(bankForm)
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to save bank');
      }
      showSuccess(`Bank successfully ${editingBank ? 'updated' : 'added'}!`);
      setBankForm({ bank_name: '', bank_code: '' });
      setShowBankModal(false);
      setEditingBank(null);
      loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  const toggleBankStatus = async (bank) => {
    const newStatus = bank.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(buildApiUrl(`/api/v1/bank-loans/banks/${bank.id}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update bank status');
      }
      showSuccess(`Bank status updated to ${newStatus}!`);
      loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  // Loan handlers
  const handleAddLoanEntry = () => {
    setLoanEntries(prev => {
      const last = prev[prev.length - 1];
      return [...prev, createEmptyLoanEntry(last)];
    });
  };

  const handleRemoveLoanEntry = (index) => {
    if (loanEntries.length <= 1) return;
    setLoanEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleLoanEntryChange = (index, field, value) => {
    setLoanEntries(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleLoanSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEmployeeId) {
      showWarning('Please select an employee');
      return;
    }

    if (!loanEntries || loanEntries.length === 0) {
      showWarning('Please add at least one loan');
      return;
    }

    // Validate each loan entry
    for (let i = 0; i < loanEntries.length; i++) {
      const entry = loanEntries[i];
      const loanNum = loanEntries.length > 1 ? ` for Loan #${i + 1}` : '';
      if (!entry.bank_id) {
        showWarning(`Please select a bank${loanNum}`);
        return;
      }
      const ded = parseFloat(entry.monthly_deduction_amount);
      if (isNaN(ded) || ded <= 0) {
        showWarning(`Please enter a valid monthly deduction amount${loanNum}`);
        return;
      }
      if (!entry.effective_month || !entry.effective_year) {
        showWarning(`Please select effective month and year${loanNum}`);
        return;
      }
    }

    setIsSubmittingLoan(true);
    try {
      if (editingLoan) {
        const entry = loanEntries[0];
        const rawOriginal = entry.original_loan_amount;
        const parsedOriginal = rawOriginal !== '' && rawOriginal !== null && !isNaN(parseFloat(rawOriginal))
          ? parseFloat(rawOriginal)
          : null;

        const payload = {
          employee_id: parseInt(selectedEmployeeId),
          bank_id: parseInt(entry.bank_id),
          original_loan_amount: parsedOriginal,
          monthly_deduction_amount: parseFloat(entry.monthly_deduction_amount),
          effective_month: parseInt(entry.effective_month),
          effective_year: parseInt(entry.effective_year),
          loan_reference_number: entry.loan_reference_number || null
        };

        const res = await fetch(buildApiUrl(`/api/v1/bank-loans/${editingLoan.id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || 'Failed to update loan');
        }

        showSuccess('Employee bank loan successfully updated!');
      } else {
        const payload = {
          employee_id: parseInt(selectedEmployeeId),
          loans: loanEntries.map(entry => {
            const rawOriginal = entry.original_loan_amount;
            const parsedOriginal = rawOriginal !== '' && rawOriginal !== null && !isNaN(parseFloat(rawOriginal))
              ? parseFloat(rawOriginal)
              : null;
            return {
              bank_id: parseInt(entry.bank_id),
              original_loan_amount: parsedOriginal,
              monthly_deduction_amount: parseFloat(entry.monthly_deduction_amount),
              effective_month: parseInt(entry.effective_month),
              effective_year: parseInt(entry.effective_year),
              loan_reference_number: entry.loan_reference_number || null
            };
          })
        };

        const res = await fetch(buildApiUrl('/api/v1/bank-loans/multiple'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || 'Failed to save loan(s)');
        }

        const count = loanEntries.length;
        showSuccess(
          count > 1
            ? `Successfully created ${count} bank loans for employee!`
            : 'Employee bank loan successfully created!'
        );
      }

      setShowLoanModal(false);
      setEditingLoan(null);
      setSelectedEmployeeId('');
      setLoanEntries([createEmptyLoanEntry()]);
      loadData();
    } catch (err) {
      showError(err.message);
    } finally {
      setIsSubmittingLoan(false);
    }
  };

  const handleStopRecovery = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(buildApiUrl(`/api/v1/bank-loans/${selectedLoanForStop.id}/stop-recovery?stop_month=${stopForm.stop_month}&stop_year=${stopForm.stop_year}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to stop recovery');
      }
      showSuccess('Recovery stopped successfully!');
      setShowStopModal(false);
      setSelectedLoanForStop(null);
      loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  // Filter computations
  const filteredLoans = loans.filter(loan => {
    const emp = loan.employee;
    const matchesSearch = !searchTerm ||
      (emp && (
        emp.emp_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.first_name + ' ' + emp.last_name).toLowerCase().includes(searchTerm.toLowerCase())
      )) ||
      (loan.loan_reference_number && loan.loan_reference_number.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesBank = bankFilter === 'all' || String(loan.bank_id) === String(bankFilter);
    const matchesStatus = statusFilter === 'all' || loan.status === statusFilter;

    return matchesSearch && matchesBank && matchesStatus;
  });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-blue-500 rounded-xl text-white">
              <Landmark className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bank Loan Management</h1>
              <p className="text-sm text-gray-500">Configure bank master and manage employee bank loan deductions</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {activeTab === 'loans' && hasPermission('bank:loan:add') && (
            <>
              <button
                onClick={() => setShowBulkUploadModal(true)}
                className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition shadow-sm font-semibold text-sm"
              >
                <UploadCloud className="w-4 h-4 mr-2" /> Bulk Upload
              </button>
              <button
                onClick={() => {
                  setEditingLoan(null);
                  setSelectedEmployeeId('');
                  setLoanEntries([createEmptyLoanEntry()]);
                  setShowLoanModal(true);
                }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm font-semibold text-sm"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Bank Loan
              </button>
            </>
          )}
          {activeTab === 'banks' && hasPermission('bank:add') && (
            <button
              onClick={() => {
                setEditingBank(null);
                setBankForm({ bank_name: '', bank_code: '' });
                setShowBankModal(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm font-semibold text-sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Bank
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {(canViewLoans || canViewBanks) && (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {canViewLoans && (
              <button
                onClick={() => setActiveTab('loans')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-all ${
                  activeTab === 'loans'
                    ? 'border-blue-500 text-blue-600 font-semibold'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Employee Bank Loans
              </button>
            )}
            {canViewBanks && (
              <button
                onClick={() => setActiveTab('banks')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-all ${
                  activeTab === 'banks'
                    ? 'border-blue-500 text-blue-600 font-semibold'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Bank Master
              </button>
            )}
          </nav>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : activeTab === 'loans' ? (
        <>
          {canViewLoans ? (
            <div className="space-y-4">
          {/* Filters Bar */}
          <div className="bg-white p-4 rounded-xl border border-gray-100 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search by employee name, code or ref no..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-semibold text-gray-500 uppercase">Bank:</span>
              </div>
              <select
                value={bankFilter}
                onChange={(e) => setBankFilter(e.target.value)}
                className="border border-gray-200 rounded-lg py-1.5 px-3 text-sm bg-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Banks</option>
                {banks.map(bank => (
                  <option key={bank.id} value={bank.id}>{bank.bank_name}</option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-200 rounded-lg py-1.5 px-3 text-sm bg-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="stopped">Stopped</option>
              </select>
            </div>
          </div>

          {/* Loans Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Bank</th>
                  <th className="px-6 py-4">Ref. Account</th>
                  <th className="px-6 py-4 text-right">Original Loan</th>
                  <th className="px-6 py-4 text-right">Monthly Deduction</th>
                  <th className="px-6 py-4">Effective Period</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-700">
                {filteredLoans.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-10 text-center text-gray-500">
                      No employee bank loans found matching the criteria.
                    </td>
                  </tr>
                ) : (
                  filteredLoans.map((loan) => (
                    <tr key={loan.id} className="hover:bg-gray-50/50 transition">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">
                          {loan.employee ? `${loan.employee.first_name} ${loan.employee.last_name}` : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 font-medium">
                          {loan.employee?.emp_code || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-800">
                        {loan.bank?.bank_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-gray-500">
                        {loan.loan_reference_number || '-'}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-gray-900">
                        {loan.original_loan_amount != null && Number(loan.original_loan_amount) > 0
                          ? `₹${Number(loan.original_loan_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-blue-600">
                        ₹{Number(loan.monthly_deduction_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-800">
                          {months.find(m => m.value === loan.effective_month)?.label} {loan.effective_year}
                        </div>
                        {loan.status === 'stopped' && (
                          <div className="text-xs text-red-500 font-semibold">
                            Stopped from: {months.find(m => m.value === loan.stopped_month)?.label} {loan.stopped_year}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          loan.status === 'active'
                            ? 'bg-green-50 text-green-700 border border-green-100'
                            : 'bg-red-50 text-red-700 border border-red-100'
                        }`}>
                          {loan.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {hasPermission('bank:loan:add') && (
                            <button
                              onClick={() => {
                                setEditingLoan(loan);
                                setSelectedEmployeeId(String(loan.employee_id));
                                setLoanEntries([{
                                  tempId: loan.id,
                                  bank_id: String(loan.bank_id),
                                  original_loan_amount: loan.original_loan_amount != null ? String(loan.original_loan_amount) : '',
                                  monthly_deduction_amount: String(loan.monthly_deduction_amount),
                                  effective_month: loan.effective_month,
                                  effective_year: loan.effective_year,
                                  loan_reference_number: loan.loan_reference_number || ''
                                }]);
                                setShowLoanModal(true);
                              }}
                              className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-blue-600 transition"
                              title="Edit Loan"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                          )}
                          {loan.status === 'active' && hasPermission('bank:loan:stop') && (
                            <button
                              onClick={() => {
                                setSelectedLoanForStop(loan);
                                setStopForm({
                                  stop_month: new Date().getMonth() + 1,
                                  stop_year: new Date().getFullYear()
                                });
                                setShowStopModal(true);
                              }}
                              className="px-2.5 py-1 text-xs font-bold text-white bg-red-500 hover:bg-red-600 rounded shadow-sm transition"
                            >
                              Stop Recovery
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        ) : (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500 border border-gray-100">
            You do not have permission to view employee bank loans.
          </div>
        )}
      </>
      ) : (
        /* Bank Master Tab */
        <>
          {canViewBanks ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-100">
              <tr>
                <th className="px-6 py-4">Bank Name</th>
                <th className="px-6 py-4">Bank Code</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {banks.map((bank) => (
                <tr key={bank.id} className="hover:bg-gray-50/50 transition">
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    {bank.bank_name}
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-gray-500">
                    {bank.bank_code || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      bank.is_active
                        ? 'bg-green-50 text-green-700 border border-green-100'
                        : 'bg-gray-50 text-gray-700 border border-gray-100'
                    }`}>
                      {bank.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center space-x-3">
                      {hasPermission('bank:edit') && (
                        <>
                          <button
                            onClick={() => {
                              setEditingBank(bank);
                              setBankForm({
                                bank_name: bank.bank_name,
                                bank_code: bank.bank_code || ''
                              });
                              setShowBankModal(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-blue-600 transition"
                            title="Edit Bank"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {bank.bank_name !== 'None' && (
                            <button
                              onClick={() => toggleBankStatus(bank)}
                              className="p-1 hover:bg-gray-100 rounded text-gray-500 transition"
                              title={bank.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {bank.is_active ? (
                                <ToggleRight className="w-6 h-6 text-green-600" />
                              ) : (
                                <ToggleLeft className="w-6 h-6 text-gray-400" />
                              )}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500 border border-gray-100">
            You do not have permission to view Bank Master.
          </div>
        )}
      </>
      )}

      {/* Add/Edit Loan Modal */}
      {showLoanModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100"
          >
            <div className="flex justify-between items-center p-5 border-b border-gray-100 bg-gray-50/70 shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {editingLoan ? 'Edit Employee Bank Loan' : 'Add Employee Bank Loan(s)'}
                </h3>
                {!editingLoan && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Select an employee and configure one or more bank loans in this form.
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowLoanModal(false)}
                className="p-1 hover:bg-gray-200 rounded-full transition"
                title="Close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleLoanSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto space-y-5 flex-1">
                {/* Employee Selection */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Employee *
                  </label>
                  <select
                    required
                    disabled={!!editingLoan}
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg p-2.5 text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        [{emp.emp_code}] {emp.full_name || emp.emp_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Loans Section Header */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div>
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      {editingLoan ? 'Loan Details' : `Loans to Add (${loanEntries.length})`}
                    </h4>
                    {!editingLoan && (
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Add one or more bank loans for the selected employee
                      </p>
                    )}
                  </div>
                  {!editingLoan && (
                    <button
                      type="button"
                      onClick={handleAddLoanEntry}
                      className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-bold transition border border-blue-200 shadow-xs"
                      title="Add another loan for this employee"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add Loan</span>
                    </button>
                  )}
                </div>

                {/* Loans List */}
                <div className="space-y-4">
                  {loanEntries.map((entry, index) => (
                    <div
                      key={entry.tempId || index}
                      className="p-4 bg-gray-50/70 border border-gray-200 rounded-xl space-y-3 relative transition hover:border-gray-300"
                    >
                      {/* Loan header with numbering & remove button */}
                      {!editingLoan && (
                        <div className="flex items-center justify-between pb-2 border-b border-gray-200/60">
                          <span className="px-2.5 py-0.5 text-xs font-bold rounded-md bg-blue-100 text-blue-800">
                            Loan #{index + 1}
                          </span>
                          {loanEntries.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveLoanEntry(index)}
                              className="inline-flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded transition font-medium"
                              title="Remove this loan"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Remove</span>
                            </button>
                          )}
                        </div>
                      )}

                      {/* Bank Select */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                          Bank *
                        </label>
                        <select
                          required
                          value={entry.bank_id}
                          onChange={(e) => handleLoanEntryChange(index, 'bank_id', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        >
                          <option value="">Select Bank</option>
                          {banks
                            .filter(b => b.is_active || (editingLoan && String(b.id) === String(entry.bank_id)))
                            .map(bank => (
                              <option key={bank.id} value={bank.id}>{bank.bank_name}</option>
                            ))}
                        </select>
                      </div>

                      {/* Deduction and Original Amount */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                            Monthly Deduction Amount *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            required
                            value={entry.monthly_deduction_amount}
                            onChange={(e) => handleLoanEntryChange(index, 'monthly_deduction_amount', e.target.value)}
                            placeholder="e.g. 8000"
                            className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                            Original Loan Amount (Optional)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={entry.original_loan_amount}
                            onChange={(e) => handleLoanEntryChange(index, 'original_loan_amount', e.target.value)}
                            placeholder="Optional, e.g. 500000"
                            className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                      </div>

                      {/* Month and Year */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                            Effective Month *
                          </label>
                          <select
                            value={entry.effective_month}
                            onChange={(e) => handleLoanEntryChange(index, 'effective_month', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          >
                            {months.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                            Effective Year *
                          </label>
                          <select
                            value={entry.effective_year}
                            onChange={(e) => handleLoanEntryChange(index, 'effective_year', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          >
                            {years.map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Loan Ref */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                          Loan Ref/Account Number
                        </label>
                        <input
                          type="text"
                          value={entry.loan_reference_number}
                          onChange={(e) => handleLoanEntryChange(index, 'loan_reference_number', e.target.value)}
                          placeholder="e.g. ACC1002345"
                          className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  ))}

                  {/* Add Another Loan Button at bottom */}
                  {!editingLoan && (
                    <button
                      type="button"
                      onClick={handleAddLoanEntry}
                      className="w-full py-2.5 px-4 border-2 border-dashed border-blue-200 hover:border-blue-400 rounded-xl text-blue-600 hover:text-blue-700 bg-blue-50/40 hover:bg-blue-50 font-semibold text-xs flex items-center justify-center space-x-1.5 transition"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Another Loan for this Employee</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-100 bg-gray-50/70 flex justify-between items-center shrink-0">
                <div className="text-xs text-gray-500 font-medium">
                  {!editingLoan && loanEntries.length > 1 && (
                    <span>{loanEntries.length} loans configured</span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowLoanModal(false)}
                    className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-100 text-sm font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingLoan}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold shadow-sm transition flex items-center space-x-2 disabled:opacity-60"
                  >
                    <span>
                      {isSubmittingLoan
                        ? 'Saving...'
                        : editingLoan
                        ? 'Update Loan'
                        : loanEntries.length > 1
                        ? `Save ${loanEntries.length} Loans`
                        : 'Save Loan'}
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Add/Edit Bank Modal */}
      {showBankModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-100"
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                {editingBank ? 'Edit Bank Master' : 'Add Bank Master'}
              </h3>
              <button onClick={() => setShowBankModal(false)} className="p-1 hover:bg-gray-200 rounded-full transition">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleBankSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bank Name *</label>
                <input
                  type="text"
                  required
                  value={bankForm.bank_name}
                  onChange={(e) => setBankForm(prev => ({ ...prev, bank_name: e.target.value }))}
                  placeholder="e.g. HDFC Bank"
                  className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bank Code</label>
                <input
                  type="text"
                  value={bankForm.bank_code}
                  onChange={(e) => setBankForm(prev => ({ ...prev, bank_code: e.target.value }))}
                  placeholder="e.g. HDFC"
                  className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="pt-4 border-t border-gray-100 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowBankModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold shadow-sm"
                >
                  Save Bank
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Stop Recovery Modal */}
      {showStopModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-100"
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">Stop Loan Recovery</h3>
              <button onClick={() => setShowStopModal(false)} className="p-1 hover:bg-gray-200 rounded-full transition">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleStopRecovery} className="p-6 space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start space-x-3 text-yellow-700 text-sm">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Attention:</strong> This will stop future recovery for this loan record from the selected month onward. Finalized past payroll records will remain untouched.
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Stop from Month *</label>
                  <select
                    value={stopForm.stop_month}
                    onChange={(e) => setStopForm(prev => ({ ...prev, stop_month: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {months.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Stop from Year *</label>
                  <select
                    value={stopForm.stop_year}
                    onChange={(e) => setStopForm(prev => ({ ...prev, stop_year: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowStopModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold shadow-sm"
                >
                  Confirm Stop
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      <BulkBankLoanModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          loadData();
          showSuccess('Bank loan bulk upload processed successfully!');
        }}
      />
    </div>
  );
};

export default BankLoanManagement;
